// material-ui
import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { set } from 'lodash-es';
import React, { ReactElement } from 'react';

// apis
import {
  IBillingDetailTiers,
  IBillingDetailTierSetting,
} from '~/src/dash/api/billing-detail/billing-detail-models';
import { IPublisher } from '~/src/dash/api/console-api';
// components
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { ModelPrimaryContainerV2 } from '~/src/dash/components/model-grid-container-v2';
import { SettingsAppBar } from '~/src/dash/components/settings-app-bar';
import { Panel } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
// views
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  billingDetailTiers: IBillingDetailTiers;
  publishers: Array<Partial<IPublisher>>;
  isUpdating: boolean;
  onChange: (tiers: IBillingDetailTiers) => void; // updates parent state and rerenders
  onSubmit: (tiers: IBillingDetailTiers) => void; // saves/creates billing-detail-tiers
  onClose: () => void; // navigates to parent order or line item list
}

interface IState extends IBaseState {
  // error msging to user
  snackbarMessage: string;
  snackbarError: boolean;
}

export const BillingDetailTiersSettingsForm = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      addTypography: {
        fontSize: 16,
        fontWeight: 500,
        color: '#5865E8',
        marginLeft: 5,
        paddingBottom: 15,
        cursor: 'pointer',
      },
      formRow: {
        margin: 8,
      },
      inputLabel: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'black',
        width: '170px',
      },
      itemTypography: {
        fontSize: 16,
        fontWeight: 400,
      },
      maxWidthGrid: {
        maxWidth: 1000,
        marginTop: 10,
        marginBottom: 10,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      snackbarError: false,
      snackbarMessage: '',
    };

    // -------- SNACKBAR HELPER FUNCTIONS -------- //
    handleSnackbarError = (snackbarError: boolean, snackbarMessage: string): void => {
      this.setState({
        snackbarError,
        snackbarMessage,
      });
    };

    handleSnackbarClose = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    genPublisherRenderString = (pbID: string) => {
      const pb = this.props.publishers.filter(
        (publisher: Partial<IPublisher>) => pbID === publisher.ID,
      );

      if (pb.length > 0) {
        return `[${pb[0].ID}] ${pb[0].Name}`;
      }
      return '';
    };

    // -------- FORM UPDATE FUNCTIONS -------- //

    // a generic form handler for form input elements.
    handleFormChange = (elem: React.ChangeEvent<HTMLInputElement>): void => {
      const { type, name: path, value, checked } = elem.target;
      let parsedValue;
      switch (type) {
        // for input[type=number] fields
        case 'number':
          parsedValue = parsedValue = Number(value);
          break;
        // <Checkbox/> returned a boolean on the checked property of the target
        case 'checkbox':
          parsedValue = checked;
          break;
        // <RadioGroup/> return the value of the <Radio> element
        case 'radio':
          parsedValue = path === 'AudienceTargetingOperationType' ? parseInt(value, 10) : value;
          break;
        // Everything else including <input> fields return strings
        default:
          parsedValue = value;
      }

      // Create a new billing detail tiers so that we can bubble up change
      const newBillingDetailTiers = { ...this.props.billingDetailTiers };

      // path can be a top level deal property like "Name" for Order.Name or go deeper such as
      // "SspParams.PackageID" for Deal.SspParams.PackageID
      set<IBillingDetailTiers>(newBillingDetailTiers, path, parsedValue);

      // bubble up event
      this.props.onChange(newBillingDetailTiers);
    };

    handleDeleteBillingDetailTierSetting = (idx: number) => {
      // Create a new billing detail tiers so that we can bubble up change
      const newBillingDetailTiers = { ...this.props.billingDetailTiers };

      newBillingDetailTiers.BillingDetailTierSettings.splice(idx, 1);

      // bubble up event
      this.props.onChange(newBillingDetailTiers);
    };

    handleAddDefaultTierSettings = () => {
      // Create a new billing detail tiers so that we can bubble up change
      const newBillingDetailTiers = { ...this.props.billingDetailTiers };

      // add 5 tiers into billing detail tiers
      newBillingDetailTiers.BillingDetailTierSettings = [
        {
          ID: 0,
          PricingTier: '1',
          TiersImpressionMax: 2000000,
          TierPrice: 1.5,
        } as IBillingDetailTierSetting,
        {
          ID: 0,
          PricingTier: '2',
          TiersImpressionMax: 10000000,
          TierPrice: 1.25,
        } as IBillingDetailTierSetting,
        {
          ID: 0,
          PricingTier: '3',
          TiersImpressionMax: 50000000,
          TierPrice: 1,
        } as IBillingDetailTierSetting,
        {
          ID: 0,
          PricingTier: '4',
          TiersImpressionMax: 250000000,
          TierPrice: 0.75,
        } as IBillingDetailTierSetting,
        {
          ID: 0,
          PricingTier: '5',
          TiersImpressionMax: 10000000000,
          TierPrice: 0.5,
        } as IBillingDetailTierSetting,
      ];

      // bubble up event
      this.props.onChange(newBillingDetailTiers);
    };

    handleAddBillingDetailTierSetting = () => {
      // Create a new billing detail tiers so that we can bubble up change
      const newBillingDetailTiers = { ...this.props.billingDetailTiers };
      const newBillingDetailTierSetting = {
        ID: 0,
        PricingTier: '' + (newBillingDetailTiers.BillingDetailTierSettings.length + 1),
        TiersImpressionMax: 0,
        TierPrice: 0,
      } as IBillingDetailTierSetting;

      newBillingDetailTiers.BillingDetailTierSettings.push(newBillingDetailTierSetting);

      // bubble up event
      this.props.onChange(newBillingDetailTiers);
    };

    // -------- RENDER HELPER FUNCTIONS -------- //
    // NOTE: We destructure props and states used in render functions to clearly
    // show what we are passing as well as keeping the code clean and easier to read

    // renders top bar => name edit, save, close, delete
    renderTopBar = (): ReactElement => {
      const { billingDetailTiers, isUpdating, onSubmit, onClose } = this.props;

      return (
        <Grid item>
          <SettingsAppBar
            hasWriteRights={true}
            isUpdating={isUpdating}
            handleSave={() => onSubmit(billingDetailTiers)}
            handleClose={onClose}
            relative
          />
        </Grid>
      );
    };

    renderBillingDetailTierSetting = (
      billingDetailTierSetting: IBillingDetailTierSetting,
      idx: number,
    ): ReactElement => {
      const { classes } = this.props;

      return (
        <Grid item className={this.props.classes.maxWidthGrid}>
          <ModelPrimaryContainerV2>
            <Grid container direction='row' justify='space-between' spacing={4}>
              <Grid item xs={3} style={{ marginLeft: 15 }}>
                <Typography className={this.props.classes.typographyFont}>
                  Tier {idx + 1}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <DeleteIcon onClick={() => this.handleDeleteBillingDetailTierSetting(idx)} />
              </Grid>
            </Grid>

            {/* PricingTier */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Pricing Tier</Typography>
              </Grid>
              <Grid item xs={8}>
                <Select
                  key='PricingTier'
                  fullWidth
                  label='PricingTier'
                  name='PricingTier'
                  style={{ width: 75 }}
                  value={billingDetailTierSetting.PricingTier}
                  onChange={(elem: React.ChangeEvent<HTMLInputElement>): void => {
                    const newBillingDetailTiers = { ...this.props.billingDetailTiers };
                    newBillingDetailTiers.BillingDetailTierSettings[idx].PricingTier =
                      elem.target.value;
                    this.props.onChange(newBillingDetailTiers);
                  }}
                >
                  {['1', '2', '3', '4', '5'].map((tier: string): ReactElement => {
                    return (
                      <MenuItem key={tier} value={tier} style={{ width: '100%' }}>
                        {tier}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
            </Grid>

            {/* TiersImpressionMax */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Tiers Impression Max</Typography>
              </Grid>
              <Grid item xs={8}>
                <FocusedTextfield
                  key='TiersImpressionMax'
                  fullWidth
                  label=''
                  type='number'
                  name='TiersImpressionMax'
                  value={billingDetailTierSetting.TiersImpressionMax}
                  onChange={(elem: React.ChangeEvent<HTMLInputElement>): void => {
                    const newBillingDetailTiers = { ...this.props.billingDetailTiers };
                    newBillingDetailTiers.BillingDetailTierSettings[idx].TiersImpressionMax =
                      Number(elem.target.value);
                    this.props.onChange(newBillingDetailTiers);
                  }}
                />
              </Grid>
            </Grid>

            {/* TierPrice */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Tier Price</Typography>
              </Grid>
              <Grid item xs={8}>
                <FocusedTextfield
                  key='TierPrice'
                  fullWidth
                  label=''
                  type='number'
                  name='TierPrice'
                  value={billingDetailTierSetting.TierPrice}
                  onChange={(elem: React.ChangeEvent<HTMLInputElement>): void => {
                    const newBillingDetailTiers = { ...this.props.billingDetailTiers };
                    newBillingDetailTiers.BillingDetailTierSettings[idx].TierPrice = Number(
                      elem.target.value,
                    );
                    this.props.onChange(newBillingDetailTiers);
                  }}
                />
              </Grid>
            </Grid>
          </ModelPrimaryContainerV2>
        </Grid>
      );
    };

    renderDetails = (): ReactElement => {
      const { classes, billingDetailTiers, publishers } = this.props;

      return (
        <Grid
          item
          style={{
            width: '100%',
            marginTop: 15,
            marginLeft: 20,
            height: '100%',
            paddingBottom: 50,
          }}
        >
          <Grid container spacing={2} direction='column'>
            <Grid
              item
              style={{
                width: '100%',
                maxWidth: 800,
                marginTop: 15,
                marginLeft: 25,
              }}
            >
              {/* Publisher */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Publisher</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    key='PublisherID'
                    fullWidth
                    label='Publisher'
                    name='PublisherID'
                    style={{ width: 200 }}
                    value={billingDetailTiers.PublisherID}
                    renderValue={(selected: any) => this.genPublisherRenderString(selected)}
                    onChange={this.handleFormChange}
                  >
                    {publishers.map((publisher: Partial<IPublisher>): ReactElement => {
                      return (
                        <MenuItem key={publisher.ID} value={publisher.ID} style={{ width: '100%' }}>
                          {`[${publisher.ID}] ${publisher.Name}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
              </Grid>

              {/* Product */}
              <Grid container alignItems='center' spacing={2} className={classes.formRow}>
                <Grid item xs={2}>
                  <Typography className={classes.itemTypography}>Product</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    key='Product'
                    fullWidth
                    label='Product'
                    name='Product'
                    style={{ width: 200 }}
                    value={billingDetailTiers.Product}
                    onChange={this.handleFormChange}
                  >
                    {['Ad Platform', 'SSAI', 'Audience Manager'].map(
                      (product: string): ReactElement => {
                        return (
                          <MenuItem key={product} value={product} style={{ width: '100%' }}>
                            {product}
                          </MenuItem>
                        );
                      },
                    )}
                  </Select>
                </Grid>
              </Grid>

              {/* Billing Detail Setting  */}
              {billingDetailTiers.BillingDetailTierSettings.map(
                (setting: IBillingDetailTierSetting, idx: number) =>
                  this.renderBillingDetailTierSetting(setting, idx),
              )}

              <Grid item className={this.props.classes.maxWidthGrid} style={{ marginTop: 15 }}>
                <Grid container direction='row' justify='flex-start'>
                  {billingDetailTiers.BillingDetailTierSettings.length === 0 && (
                    <Grid item style={{ paddingRight: 5 }}>
                      <Typography
                        className={this.props.classes.addTypography}
                        onClick={this.handleAddDefaultTierSettings}
                      >
                        + Default Tier Settings
                      </Typography>
                    </Grid>
                  )}
                  {billingDetailTiers.BillingDetailTierSettings.length < 5 && (
                    <Grid item>
                      <Typography
                        className={this.props.classes.addTypography}
                        onClick={this.handleAddBillingDetailTierSetting}
                      >
                        + New Tier
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    render() {
      const { snackbarMessage, snackbarError } = this.state;

      return (
        <>
          <Panel>{this.renderTopBar()}</Panel>
          <Panel scroll largePadding>
            {this.renderDetails()}
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleSnackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </>
      );
    }
  },
);
